import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { useApi } from '../../hooks/useApi';
import { AnalyticsFirstWeb } from '../../models/analyticsFirstWeb';
import Indicator from '../../components/UI/Indicator/Indicator';
import WebinarAutocomplete from '../../components/UI/WebinarAutocomplete/WebinarAutocomplete';
import UtmAutocomplete from '../../components/UI/UtmAutocomplete/UtmAutocomplete';
import styles from './RevenueReport.module.css';

const RevenueReportPage: React.FC = observer(() => {
  const api = useApi();

  const [data, setData] = useState<AnalyticsFirstWeb | null>(null);
  const [loading, setLoading] = useState(false);

  // Форма данных
  const [formData, setFormData] = useState({
    user_reg_from: '2024-06-01 00:00:00',
    user_reg_to: '2025-06-15 23:59:59',
    web_reg_from: '2025-05-19 00:00:00',
    web_reg_to: '2025-06-15 23:59:59',
    rev_from: '2025-05-19 00:00:00',
    rev_to: '2025-06-15 23:59:59',
    webinar_names: ['ANTI AGE | ВЕБИНАР'],
    utm_sources: ['fb', 'aminova', 'Aminova'],
    utm_mediums: [] as string[],
    utm_campaigns: [] as string[],
  });

  const [webinarName, setWebinarName] = useState('');
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.analytics.getRevenueReport(formData);
      if (response.success && response.body) {
        setData(response.body);
      } else {
        // eslint-disable-next-line no-console
        console.error('Ответ API не содержит данных:', response);
        setData(null);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Ошибка при получении данных:', error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const addWebinarName = (name: string) => {
    if (name.trim() && !formData.webinar_names.includes(name.trim())) {
      setFormData((prev) => ({
        ...prev,
        webinar_names: [...prev.webinar_names, name.trim()],
      }));
    }
  };

  const addWebinarNameFromInput = () => {
    if (webinarName.trim()) {
      addWebinarName(webinarName);
      setWebinarName('');
    }
  };

  const removeWebinarName = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      webinar_names: prev.webinar_names.filter((_, i) => i !== index),
    }));
  };

  const addUtmSource = () => {
    if (utmSource.trim()) {
      setFormData((prev) => ({
        ...prev,
        utm_sources: [...prev.utm_sources, utmSource.trim()],
      }));
      setUtmSource('');
    }
  };

  const addUtmSourceFromAutocomplete = (source: string) => {
    if (source && !formData.utm_sources.includes(source)) {
      setFormData((prev) => ({
        ...prev,
        utm_sources: [...prev.utm_sources, source],
      }));
      setUtmSource('');
    }
  };

  const removeUtmSource = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      utm_sources: prev.utm_sources.filter((_, i) => i !== index),
    }));
  };

  const addUtmMedium = () => {
    if (utmMedium.trim()) {
      setFormData((prev) => ({
        ...prev,
        utm_mediums: [...prev.utm_mediums, utmMedium.trim()],
      }));
      setUtmMedium('');
    }
  };

  const addUtmMediumFromAutocomplete = (value: string) => {
    if (value.trim() && !formData.utm_mediums.includes(value.trim())) {
      setFormData((prev) => ({
        ...prev,
        utm_mediums: [...prev.utm_mediums, value.trim()],
      }));
      setUtmMedium('');
    }
  };

  const removeUtmMedium = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      utm_mediums: prev.utm_mediums.filter((_, i) => i !== index),
    }));
  };

  const addUtmCampaign = () => {
    if (utmCampaign.trim()) {
      setFormData((prev) => ({
        ...prev,
        utm_campaigns: [...prev.utm_campaigns, utmCampaign.trim()],
      }));
      setUtmCampaign('');
    }
  };

  const addUtmCampaignFromAutocomplete = (value: string) => {
    if (value.trim() && !formData.utm_campaigns.includes(value.trim())) {
      setFormData((prev) => ({
        ...prev,
        utm_campaigns: [...prev.utm_campaigns, value.trim()],
      }));
      setUtmCampaign('');
    }
  };

  const removeUtmCampaign = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      utm_campaigns: prev.utm_campaigns.filter((_, i) => i !== index),
    }));
  };

  return (
    <div className={styles.container}>
      <h1>Отчет по первому вебинару</h1>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label>Регистрация пользователей с:</label>
          <input
            type="datetime-local"
            value={formData.user_reg_from.replace(' ', 'T').slice(0, -3)}
            onChange={(e) =>
              handleInputChange(
                'user_reg_from',
                e.target.value.replace('T', ' ') + ':00',
              )
            }
          />
        </div>

        <div className={styles.formGroup}>
          <label>Регистрация пользователей до:</label>
          <input
            type="datetime-local"
            value={formData.user_reg_to.replace(' ', 'T').slice(0, -3)}
            onChange={(e) =>
              handleInputChange(
                'user_reg_to',
                e.target.value.replace('T', ' ') + ':59',
              )
            }
          />
        </div>

        <div className={styles.formGroup}>
          <label>Веб-регистрация с:</label>
          <input
            type="datetime-local"
            value={formData.web_reg_from.replace(' ', 'T').slice(0, -3)}
            onChange={(e) =>
              handleInputChange(
                'web_reg_from',
                e.target.value.replace('T', ' ') + ':00',
              )
            }
          />
        </div>

        <div className={styles.formGroup}>
          <label>Веб-регистрация до:</label>
          <input
            type="datetime-local"
            value={formData.web_reg_to.replace(' ', 'T').slice(0, -3)}
            onChange={(e) =>
              handleInputChange(
                'web_reg_to',
                e.target.value.replace('T', ' ') + ':59',
              )
            }
          />
        </div>

        <div className={styles.formGroup}>
          <label>Выручка с:</label>
          <input
            type="datetime-local"
            value={formData.rev_from.replace(' ', 'T').slice(0, -3)}
            onChange={(e) =>
              handleInputChange(
                'rev_from',
                e.target.value.replace('T', ' ') + ':00',
              )
            }
          />
        </div>

        <div className={styles.formGroup}>
          <label>Выручка до:</label>
          <input
            type="datetime-local"
            value={formData.rev_to.replace(' ', 'T').slice(0, -3)}
            onChange={(e) =>
              handleInputChange(
                'rev_to',
                e.target.value.replace('T', ' ') + ':59',
              )
            }
          />
        </div>

        <div className={styles.formGroup}>
          <label>Названия вебинаров:</label>
          <WebinarAutocomplete
            value={webinarName}
            onChange={setWebinarName}
            onSelect={addWebinarName}
            placeholder="Начните вводить название вебинара..."
          />
          <div className={styles.arrayInput}>
            <input
              type="text"
              value={webinarName}
              onChange={(e) => setWebinarName(e.target.value)}
              placeholder="Или введите название вручную"
            />
            <button type="button" onClick={addWebinarNameFromInput}>
              Добавить
            </button>
          </div>
          <div className={styles.tags}>
            {formData.webinar_names.map((name, index) => (
              <span key={index} className={styles.tag}>
                {name}
                <button type="button" onClick={() => removeWebinarName(index)}>
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label>UTM источники:</label>
          <UtmAutocomplete
            type="utm_source"
            value={utmSource}
            onChange={setUtmSource}
            onSelect={addUtmSourceFromAutocomplete}
            placeholder="Начните вводить UTM источник..."
          />
          <div className={styles.arrayInput}>
            <input
              type="text"
              value={utmSource}
              onChange={(e) => setUtmSource(e.target.value)}
              placeholder="Или введите UTM источник вручную"
            />
            <button type="button" onClick={addUtmSource}>
              Добавить
            </button>
          </div>
          <div className={styles.tags}>
            {formData.utm_sources.map((source, index) => (
              <span key={index} className={styles.tag}>
                {source}
                <button type="button" onClick={() => removeUtmSource(index)}>
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label>UTM медиумы:</label>
          <UtmAutocomplete
            type="utm_medium"
            value={utmMedium}
            onChange={setUtmMedium}
            onSelect={addUtmMediumFromAutocomplete}
            placeholder="Начните вводить UTM медиум..."
          />
          <div className={styles.arrayInput}>
            <input
              type="text"
              value={utmMedium}
              onChange={(e) => setUtmMedium(e.target.value)}
              placeholder="Или введите UTM медиум вручную"
            />
            <button type="button" onClick={addUtmMedium}>
              Добавить
            </button>
          </div>
          <div className={styles.tags}>
            {formData.utm_mediums.map((medium, index) => (
              <span key={index} className={styles.tag}>
                {medium}
                <button type="button" onClick={() => removeUtmMedium(index)}>
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label>UTM кампании:</label>
          <UtmAutocomplete
            type="utm_campaign"
            value={utmCampaign}
            onChange={setUtmCampaign}
            onSelect={addUtmCampaignFromAutocomplete}
            placeholder="Начните вводить UTM кампанию..."
          />
          <div className={styles.arrayInput}>
            <input
              type="text"
              value={utmCampaign}
              onChange={(e) => setUtmCampaign(e.target.value)}
              placeholder="Или введите UTM кампанию вручную"
            />
            <button type="button" onClick={addUtmCampaign}>
              Добавить
            </button>
          </div>
          <div className={styles.tags}>
            {formData.utm_campaigns.map((campaign, index) => (
              <span key={index} className={styles.tag}>
                {campaign}
                <button type="button" onClick={() => removeUtmCampaign(index)}>
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className={styles.submitButtonContainer}>
          <button
            type="submit"
            disabled={loading}
            className={styles.submitButton}
          >
            {loading ? 'Загрузка...' : 'Получить отчет'}
          </button>
        </div>
      </form>

      {data && (
        <div className={styles.results}>
          <h2>Результаты отчета</h2>

          <div className={styles.indicatorsGrid}>
            <Indicator
              name="Всего участников вебинара"
              curentValue={data.total_webinar_users || 0}
              format="number"
            />
            <Indicator
              name="Платящих клиентов"
              curentValue={data.paying_customers || 0}
              format="number"
            />
            <Indicator
              name="Конверсия"
              curentValue={parseFloat((data.conversion_rate || 0).toFixed(2))}
              format="percent"
            />
            <Indicator
              name="Количество заказов"
              curentValue={data.orders_count || 0}
              format="number"
            />
            <Indicator
              name="Общая выручка"
              curentValue={data.total_revenue || 0}
              format="currency"
              symbol="$"
            />
            <Indicator
              name="Средний чек"
              curentValue={data.average_check || 0}
              format="currency"
              symbol="$"
            />
          </div>

          {/* Показываем сообщение, если все данные пустые */}
          {(!data.contact_examples || data.contact_examples.length === 0) &&
          (!data.contacts_with_orders ||
            data.contacts_with_orders.length === 0) &&
          (!data.payment_groups || data.payment_groups.length === 0) &&
          (!data.category_revenue || data.category_revenue.length === 0) &&
          data.total_webinar_users === 0 ? (
            <div className={styles.emptyState}>
              <h3>Нет данных по заданным фильтрам</h3>
              <p>
                Попробуйте изменить параметры поиска или расширить временной
                диапазон.
              </p>
            </div>
          ) : null}

          <div className={styles.section}>
            <h3>Контакты из фильтра ({data.contact_examples?.length || 0})</h3>
            <div className={styles.contactsList}>
              {data.contact_examples && data.contact_examples.length > 0 ? (
                data.contact_examples.map((contact) => (
                  <a
                    key={contact.id}
                    href={`https://admin.accelonline.io/crm/contacts/${contact.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.contactItem}
                  >
                    <strong>{contact.first_name}</strong> - {contact.email}
                  </a>
                ))
              ) : (
                <div className={styles.noData}>Нет данных для отображения</div>
              )}
            </div>
          </div>

          <div className={styles.section}>
            <h3>
              Контакты с заказами ({data.contacts_with_orders?.length || 0})
            </h3>
            <div className={styles.contactsList}>
              {data.contacts_with_orders &&
              data.contacts_with_orders.length > 0 ? (
                data.contacts_with_orders.map((contact) => (
                  <a
                    key={contact.id}
                    href={`https://admin.accelonline.io/crm/contacts/${contact.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.contactItem}
                  >
                    <strong>{contact.first_name}</strong> - {contact.email}
                  </a>
                ))
              ) : (
                <div className={styles.noData}>Нет данных для отображения</div>
              )}
            </div>
          </div>

          <div className={styles.section}>
            <h3>Группы платежей</h3>
            <div className={styles.paymentGroups}>
              {data.payment_groups && data.payment_groups.length > 0 ? (
                data.payment_groups.map((group, index) => (
                  <div key={index} className={styles.paymentGroup}>
                    <h4>{group.group}</h4>
                    <p>Выручка: ${group.revenue}</p>
                    <p>Заказы: {group.orders}</p>
                    <p>Процент: {group.percentage.toFixed(2)}%</p>
                  </div>
                ))
              ) : (
                <div className={styles.noData}>Нет данных для отображения</div>
              )}
            </div>
          </div>

          <div className={styles.section}>
            <h3>Выручка по категориям</h3>
            <div className={styles.categoryRevenue}>
              {data.category_revenue && data.category_revenue.length > 0 ? (
                data.category_revenue.map((category, index) => (
                  <div key={index} className={styles.categoryItem}>
                    <h4>{category.category}</h4>
                    <p>Сумма: ${category.amount}</p>
                    <p>Процент: {category.percentage.toFixed(2)}%</p>
                  </div>
                ))
              ) : (
                <div className={styles.noData}>Нет данных для отображения</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default RevenueReportPage;
